import React, { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { entitiesControllerGetEntityTree } from 'api.gen'
import type { EntityTreeNode } from 'api.gen'
import { ButtonIcon, ListItem, Search, Tag } from '@arvoreeducacao/bonsai-ds'
import { useTranslation } from 'i18n'
import formatDegree from 'utils/degree'
import {
  HierarchyWrapper,
  HierarchyHeader,
  HierarchyContent,
  HierarchyList
} from './TeacherClassHierarchySelector.styles'

interface TeacherClassHierarchySelectorProps {
  selectedClassIds: number[]
  onClassToggle: (
    classId: number,
    checked: boolean,
    hierarchyPath?: NavigationStackItem[]
  ) => void
}

interface NavigationStackItem {
  id: number
  name: string
  type: string
}

function findNodeById(
  node: EntityTreeNode | undefined,
  id: number
): EntityTreeNode | undefined {
  if (!node) return undefined
  if (Number(node.id) === id) return node
  if (node.children) {
    for (const child of node.children) {
      const found = findNodeById(child, id)
      if (found) return found
    }
  }
  return undefined
}

const TeacherClassHierarchySelector = ({
  selectedClassIds,
  onClassToggle
}: TeacherClassHierarchySelectorProps) => {
  const { t } = useTranslation()
  const [navigationStack, setNavigationStack] = useState<NavigationStackItem[]>(
    []
  )
  const [search, setSearch] = useState('')

  const { data: treeData, isLoading } = useQuery({
    queryKey: ['entity-tree-selector'],
    queryFn: () => entitiesControllerGetEntityTree()
  })

  const rootNode = treeData?.data?.root

  const currentChildren = useMemo(() => {
    if (!rootNode) return []

    if (navigationStack.length === 0) {
      if (rootNode.children && rootNode.children.length > 0) {
        return rootNode.children.map(child => ({
          id: Number(child.id),
          name: child.name,
          type: child.entity_type,
          degree: child.degree,
          hasChildren: (child.children && child.children.length > 0) || false
        }))
      }
      return [
        {
          id: Number(rootNode.id),
          name: rootNode.name,
          type: rootNode.entity_type,
          degree: rootNode.degree,
          hasChildren:
            (rootNode.children && rootNode.children.length > 0) || false
        }
      ]
    }

    const currentEntityId = navigationStack[navigationStack.length - 1].id
    const currentNode = findNodeById(rootNode, currentEntityId)

    if (!currentNode?.children) return []

    return currentNode.children.map(child => ({
      id: Number(child.id),
      name: child.name,
      type: child.entity_type,
      degree: child.degree,
      hasChildren: (child.children && child.children.length > 0) || false
    }))
  }, [rootNode, navigationStack])

  const filteredChildren = currentChildren.filter(child =>
    child.name.toLowerCase().includes(search.toLowerCase())
  )

  const handleEntityClick = (entity: {
    id: number
    name: string
    type: string
    hasChildren: boolean
  }) => {
    if (entity.type === 'class') {
      const isSelected = selectedClassIds.includes(entity.id)
      const hierarchyPath = [
        ...navigationStack,
        {
          id: entity.id,
          name: entity.name,
          type: entity.type
        }
      ]
      onClassToggle(entity.id, !isSelected, hierarchyPath)
    } else if (entity.hasChildren) {
      setNavigationStack(prev => [
        ...prev,
        {
          id: entity.id,
          name: entity.name,
          type: entity.type
        }
      ])
      setSearch('')
    }
  }

  const handleGoBack = () => {
    if (navigationStack.length > 0) {
      setNavigationStack(prev => prev.slice(0, -1))
      setSearch('')
    }
  }

  const canGoBack = navigationStack.length > 0
  const currentEntity = navigationStack[navigationStack.length - 1]
  const currentEntityType = currentEntity?.type || rootNode?.entity_type || ''

  const getEntityTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      holding: 'Holding',
      trademark: 'Marca',
      network: t('Rede'),
      school: t('Unidade'),
      class: t('Turma')
    }
    return labels[type] || type
  }

  return (
    <HierarchyWrapper>
      {canGoBack && (
        <HierarchyHeader>
          <ButtonIcon
            iconName="arrow-left"
            theme={{ type: 'secondary', size: 'medium' }}
            onClick={handleGoBack}
            aria-label={t('Voltar')}
          />
          <div>
            <div className="entity-name">{currentEntity?.name}</div>
            <div className="entity-type">
              {getEntityTypeLabel(currentEntityType)}
            </div>
          </div>
        </HierarchyHeader>
      )}

      <div style={{ padding: '8px' }}>
        <Search
          placeholder={t('Pesquisar')}
          mode="secondary"
          value={search}
          onChange={value => setSearch(value)}
        />
      </div>

      <HierarchyContent>
        {isLoading ? (
          <div style={{ padding: '24px', textAlign: 'center' }}>
            {t('Carregando...')}
          </div>
        ) : filteredChildren.length === 0 ? (
          <div style={{ padding: '24px', textAlign: 'center' }}>
            Nenhum resultado encontrado
          </div>
        ) : (
          <HierarchyList>
            {filteredChildren.map(child => {
              const isClass = child.type === 'class'
              const isSelected = isClass && selectedClassIds.includes(child.id)
              const degree = (child.degree as unknown as string) || undefined

              return (
                <ListItem
                  key={child.id}
                  showArrow={!isClass && child.hasChildren}
                  text={
                    (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%'
                        }}
                      >
                        <span>{child.name}</span>
                        {isClass && degree && <Tag>{formatDegree(degree)}</Tag>}
                      </div>
                    ) as unknown as string
                  }
                  onClick={() => handleEntityClick(child)}
                  checkable={isClass}
                  initialChecked={isSelected}
                  checkboxChange={(_, checked) => {
                    if (isClass) {
                      const hierarchyPath = [
                        ...navigationStack,
                        {
                          id: child.id,
                          name: child.name,
                          type: child.type
                        }
                      ]
                      onClassToggle(child.id, checked, hierarchyPath)
                    }
                  }}
                />
              )
            })}
          </HierarchyList>
        )}
      </HierarchyContent>
    </HierarchyWrapper>
  )
}

export default TeacherClassHierarchySelector
